import { ReactNode, useMemo } from 'react';
import {
  AirDataResponse,
  AirResponseDataSummary,
  TimeFilterType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { i18next } from '@frontend/i18n';
import { TooltipHeader } from '../components/chart-tooltip-header';
import { formatTooltipTitle } from '../utils/chart-utils';

const AVERAGE_UNIT_LABEL_I18N_KEY: Partial<Record<TimeFilterType, string>> = {
  [TimeFilterType.HOURLY]: 'hour',
  [TimeFilterType.DAILY]: 'day',
  [TimeFilterType.DAY_OF_WEEK]: 'day',
};

type ChartDataGroup = {
  name: string;
  rawName: string;
  values: Record<string, number>;
};

type UseTimeBasedChartPresentationOptions = {
  data: AirDataResponse | undefined;
  selectedXAxis: TimeFilterType;
  chartData: ChartDataGroup[];
  /** When true, the per-group total from the API summary is shown in the tooltip header. */
  showTotal?: boolean;
};

type UseTimeBasedChartPresentationResult = {
  summaryByName: Record<string, AirResponseDataSummary>;
  tooltipTitlesByName: Record<string, ReactNode>;
  averageUnitLabel: string;
  tooltipHeadersByName: Record<string, JSX.Element>;
  seriesTotals: Record<string, number>;
};

/**
 * Encapsulates all derived presentation state that is common across analytics
 * bar charts with a time-based x-axis (DAILY / DAY_OF_WEEK / HOURLY).
 *
 * The caller is responsible for computing `chartData` (since the `values`
 * shape varies per chart), then passes it in so this hook can build the
 * tooltip headers and series totals.
 */
export const useTimeBasedChartPresentation = ({
  data,
  selectedXAxis,
  chartData,
  showTotal = false,
}: UseTimeBasedChartPresentationOptions): UseTimeBasedChartPresentationResult => {
  const summaryByName = useMemo(() => {
    return (
      data?.summary?.reduce<Record<string, AirResponseDataSummary>>((acc, entry) => {
        if (entry.name != null) {
          acc[entry.name] = { total: entry.total, average: entry.average, count: entry.count };
        }
        return acc;
      }, {}) ?? {}
    );
  }, [data]);

  const tooltipTitlesByName = useMemo(() => {
    return (
      data?.data?.reduce<Record<string, ReactNode>>((acc, group) => {
        const name = group.name ?? '';
        const count = selectedXAxis === TimeFilterType.DAY_OF_WEEK ? summaryByName[name]?.count : undefined;
        acc[name] = formatTooltipTitle(name, selectedXAxis, count);
        return acc;
      }, {}) ?? {}
    );
  }, [data, selectedXAxis, summaryByName]);

  const averageUnitLabel = useMemo(() => {
    return i18next.t(AVERAGE_UNIT_LABEL_I18N_KEY[selectedXAxis] ?? 'day', { ns: 'analytics' });
  }, [selectedXAxis]);

  const tooltipHeadersByName = useMemo(() => {
    return chartData.reduce<Record<string, JSX.Element>>((acc, group) => {
      const tooltipTitle = tooltipTitlesByName[group.name] ?? group.name;
      let finalFilterHintText: string | undefined;
      if (selectedXAxis === TimeFilterType.DAILY) {
        finalFilterHintText = dayjs(group.name).format('dddd');
      }

      const total =
        showTotal && summaryByName[group.name]?.total != null ? Number(summaryByName[group.name].total) : undefined;

      acc[group.name] = (
        <TooltipHeader
          tooltipTitle={tooltipTitle}
          filterHintText={finalFilterHintText}
          total={total}
          averageUnitLabel={averageUnitLabel}
        />
      );

      return acc;
    }, {});
  }, [averageUnitLabel, chartData, summaryByName, tooltipTitlesByName, showTotal, selectedXAxis]);

  const seriesTotals = useMemo(() => {
    return chartData.reduce<Record<string, number>>((acc, group) => {
      Object.entries(group.values).forEach(([key, value]) => {
        acc[key] = (acc[key] ?? 0) + value;
      });
      return acc;
    }, {});
  }, [chartData]);

  return { summaryByName, tooltipTitlesByName, averageUnitLabel, tooltipHeadersByName, seriesTotals };
};
